import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';

function PrivacyPage() {
  const { theme, currentThemeName } = useTheme();
  const { t } = useLanguage();
  const isDark = currentThemeName === 'dark';

  const containerStyle = {
    minHeight: '100vh',
    background: isDark ? '#0f172a' : '#f8fafc',
    color: theme.text || (isDark ? '#f1f5f9' : '#1e293b'),
    padding: '24px 20px 48px',
  };

  const contentStyle = {
    maxWidth: 720,
    margin: '0 auto',
  };

  const backLinkStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: theme.primary || '#3b82f6',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 24,
  };

  const heading1Style = {
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: 8,
  };

  const heading2Style = {
    fontSize: '1.15rem',
    fontWeight: 600,
    marginTop: 28,
    marginBottom: 10,
  };

  const paragraphStyle = {
    fontSize: 15,
    lineHeight: 1.7,
    marginBottom: 14,
    color: isDark ? '#cbd5e1' : '#475569',
  };

  const listStyle = {
    marginLeft: 20,
    marginBottom: 14,
  };

  const listItemStyle = {
    marginBottom: 6,
    lineHeight: 1.6,
    color: isDark ? '#cbd5e1' : '#475569',
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        <Link to="/login" style={backLinkStyle}>
          <ArrowLeft size={18} /> {t('common.back')}
        </Link>

        <h1 style={heading1Style}>{t('legal.privacyTitle')}</h1>
        <p style={paragraphStyle}>{t('legal.privacyLastUpdated').replace(/\{\{year\}\}/g, String(new Date().getFullYear()))}</p>

        <h2 style={heading2Style}>{t('legal.privacyIntro')}</h2>
        <p style={paragraphStyle}>
          Oakland AI (“we”, “our”, or “us”) is committed to protecting your privacy. This Privacy 
          Policy explains how we collect, use, and safeguard information when you use our AI business 
          assistant service for small businesses in Oakland.
        </p>

        <h2 style={heading2Style}>{t('legal.privacyDataWeCollect')}</h2>
        <p style={paragraphStyle}>We may collect:</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong>Account information:</strong> name, email, phone, business type, and other details you provide when signing up</li>
          <li style={listItemStyle}><strong>Conversation data:</strong> messages you send and AI responses, to improve the service and assist you</li>
          <li style={listItemStyle}><strong>Usage data:</strong> how you use the app (e.g., features used, language preference) to improve our product</li>
          <li style={listItemStyle}><strong>Technical data:</strong> device type, browser, IP address, and similar data necessary for security and operation</li>
        </ul>

        <h2 style={heading2Style}>{t('legal.privacyHowWeUse')}</h2>
        <p style={paragraphStyle}>
          We use this information to provide and improve the Service, personalize your experience, 
          communicate with you, ensure security and compliance, and analyze usage. We do not sell your 
          personal information to third parties.
        </p>

        <h2 style={heading2Style}>{t('legal.privacySharing')}</h2>
        <p style={paragraphStyle}>
          We may share data with service providers that help us operate the Service (e.g., hosting, 
          analytics), under contracts that protect your data. We may disclose information when required 
          by law or to protect our rights and safety.
        </p>

        <h2 style={heading2Style}>{t('legal.privacySecurity')}</h2>
        <p style={paragraphStyle}>
          We use reasonable technical and organizational measures to protect your data. No system is 
          completely secure; we encourage you to use a strong password and keep your account details 
          confidential.
        </p>

        <h2 style={heading2Style}>{t('legal.privacyRights')}</h2>
        <p style={paragraphStyle}>
          Depending on where you live, you may have rights to access, correct, delete, or port your 
          data, or to object to or restrict certain processing. You can update account information 
          in your profile and contact us for other requests.
        </p>

        <h2 style={heading2Style}>{t('legal.privacyCookies')}</h2>
        <p style={paragraphStyle}>
          We use cookies and similar technologies for authentication, preferences, and analytics. You 
          can adjust browser settings to limit cookies, though some features may not work correctly 
          without them.
        </p>

        <h2 style={heading2Style}>{t('legal.privacyChanges')}</h2>
        <p style={paragraphStyle}>
          We may update this Privacy Policy from time to time. We will post the revised policy and 
          update the “Last updated” date. Continued use of the Service after changes means you accept 
          the updated policy.
        </p>

        <h2 style={heading2Style}>{t('legal.termsContact')}</h2>
        <p style={paragraphStyle}>
          For privacy-related questions or to exercise your rights, contact us through the support or 
          contact options provided in the app or on our website.
        </p>
      </div>
    </div>
  );
}

export default PrivacyPage;
